import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { onConsentChange, readConsent, saveConsent } from "@/lib/consent";

/**
 * Banner de consentimiento (Ley 81/2019).
 *
 * Sin decisión guardada no se carga ninguna medición. La elección es granular
 * y revocable desde `/privacidad`; al retirarla el banner vuelve a aparecer.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const sync = () => setVisible(readConsent() === null);
    sync();
    return onConsentChange(sync);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-description"
      className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-primary/20 bg-background/95 p-5 shadow-2xl backdrop-blur sm:p-6">
        <h2
          id="consent-title"
          className="font-playfair text-lg font-semibold text-brown-dark sm:text-xl"
        >
          Tu privacidad, tu decisión
        </h2>
        <p
          id="consent-description"
          className="mt-1 text-sm leading-relaxed text-brown-light/90"
        >
          Usamos cookies propias y de terceros para medir cómo se usa el sitio y
          para mostrarte nuestros productos en otras plataformas. Nada de eso se
          activa hasta que vos lo aceptés.{" "}
          <Link
            to="/privacidad"
            className="text-primary-dark underline underline-offset-4 hover:text-brown"
          >
            Política de privacidad
          </Link>
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-xl border border-primary/15 p-3">
            <Checkbox
              id="consent-analytics"
              checked={analytics}
              onCheckedChange={(value) => setAnalytics(value === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="consent-analytics"
              className="cursor-pointer text-sm font-normal leading-snug"
            >
              <span className="font-medium text-brown-dark">Medición</span>
              <span className="mt-0.5 block text-xs text-brown-light/80">
                Google Analytics y Clarity: qué páginas se ven y cómo se navega.
              </span>
            </Label>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-primary/15 p-3">
            <Checkbox
              id="consent-marketing"
              checked={marketing}
              onCheckedChange={(value) => setMarketing(value === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="consent-marketing"
              className="cursor-pointer text-sm font-normal leading-snug"
            >
              <span className="font-medium text-brown-dark">Publicidad</span>
              <span className="mt-0.5 block text-xs text-brown-light/80">
                Pixel de Meta: mostrarte productos en Instagram y Facebook.
              </span>
            </Label>
          </div>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => saveConsent({ analytics, marketing })}
          >
            Guardar selección
          </Button>
          <Button
            className="rounded-full bg-primary text-brown-dark hover:bg-primary-dark"
            onClick={() => saveConsent({ analytics: true, marketing: true })}
          >
            Aceptar todo
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBanner;
